/**
 * Simulation and protocol constants shared by client and server.
 *
 * These are the *protocol-level* values both sides must agree on
 * (docs/architecture/networking.md "Time And Ticks"). Server-side tunables
 * (rates, windows) can additionally be overridden by config data
 * (docs/architecture/deployment.md "Configuration And Secrets") — but the
 * defaults live here so the client and the headless tools see one truth.
 */
/** Server simulation rate per zone (Hz). */
export const TICK_RATE = 15;
/** Duration of one simulation tick in milliseconds. */
export const TICK_MS = 1000 / TICK_RATE;
/**
 * Remote entities render this far in the past (two snapshots at the default 15 Hz). The
 * snapshot rate itself and the delay at other rates live in `snapshot-rate.ts` (D1): the
 * client derives its delay from `helloAck.snapshotHz` via `interpDelayMsFor`.
 */
export const INTERP_DELAY_MS = 133;
/** On a snapshot gap, extrapolate at most this long, then freeze. */
export const MAX_EXTRAPOLATION_MS = 100;
/** Player run speed, metres per second. */
export const MOVE_SPEED_MPS = 5;
/** Dodge dash speed, metres per second. */
export const DODGE_SPEED_MPS = 12;
/** Dodge dash / evasion window duration (~300 ms — combat.md). */
export const DODGE_DURATION_MS = 300;
/** Minimum time between dodge starts (measured from dodge start). */
export const DODGE_COOLDOWN_MS = 4000;
/** The client samples input per frame but sends at most one input per tick. */
export const CLIENT_INPUT_RATE = TICK_RATE;
/** Gateway hard rate limit for movement inputs (messages/second). */
export const MAX_INPUTS_PER_SECOND = 40;
/** Server ping cadence (networking.md "Connection Lifecycle"). */
export const HEARTBEAT_INTERVAL_MS = 5000;
/** A connection silent for this long is considered dead. */
export const CONNECTION_DEAD_MS = 15000;
/** A session is resumable with the same token for this long. */
export const RESUME_WINDOW_MS = 60000;
/** Disconnected characters linger in-world this long (anti disconnect-to-dodge). */
export const LINGER_MS = 15000;
/** Linger duration when the character was in combat. */
export const LINGER_COMBAT_MS = 30000;
/** Validator-enforced telegraph wind-up floors (networking.md, content-pipeline.md). */
export const TELEGRAPH_STANDARD_MIN_MS = 700;
export const TELEGRAPH_HEAVY_MIN_MS = 1200;
/** Player collision footprint radius (metres) for shared deterministic collision (WR.2). */
export const PLAYER_COLLISION_RADIUS_M = 0.4;
/**
 * Relaxation sweeps when resolving a circle against static shapes (WR.2). Fixed
 * so the result is bounded and identical on client and server (AD-026).
 */
export const COLLISION_RELAXATION_PASSES = 4;
//# sourceMappingURL=constants.js.map